import { useCallback, useEffect, useState } from 'react';
import { ApiError, apiGet, apiPost } from '../api/client.js';
import { Layout } from '../components/Layout.js';
import { Loader2Icon } from '../components/icons.js';

/**
 * Admin surface for branded-export document templates (letterheads / proposals /
 * legal / deck). kb_admin (Dylan / legal) uploads + versions templates here
 * WITHOUT a deploy — the export path reads the active version from the store.
 * See docs/designs/branded-document-export.md.
 */

const ERROR_FALLBACK = 'Something went wrong. Please try again.';
const CLASSES = ['letterhead', 'proposal', 'legal', 'deck'] as const;

function errorMessage(err: unknown, fallback = ERROR_FALLBACK): string {
  if (err instanceof ApiError) return err.message ?? fallback;
  return err instanceof Error ? err.message : fallback;
}

interface TemplateVersion {
  n: number;
  sizeBytes: number;
  uploadedAt: string;
  uploadedBy: string;
}
interface DocTemplate {
  id: string;
  label: string;
  templateClass: (typeof CLASSES)[number];
  entity: string | null;
  ext: 'docx' | 'pptx';
  fields: string[];
  activeVersion: number;
  versions: TemplateVersion[];
  status: 'active' | 'archived';
  updatedAt: string;
  lastActor: string;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read the file.'));
    reader.onload = () => {
      const dataUrl = String(reader.result);
      resolve(dataUrl.slice(dataUrl.indexOf(',') + 1)); // strip "data:…;base64,"
    };
    reader.readAsDataURL(file);
  });
}

export function AdminDocTemplatesPage(): JSX.Element {
  const [templates, setTemplates] = useState<DocTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // upload form
  const [id, setId] = useState('');
  const [label, setLabel] = useState('');
  const [templateClass, setTemplateClass] = useState<(typeof CLASSES)[number]>('letterhead');
  const [entity, setEntity] = useState('');
  const [fields, setFields] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    apiGet<{ templates: DocTemplate[] }>('/api/v1/admin/doc-templates')
      .then((r) => setTemplates(r.templates))
      .catch((e) => setError(errorMessage(e)))
      .finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  const upload = async (): Promise<void> => {
    setUploadError(null);
    if (!file) return setUploadError('Choose a .docx or .pptx file.');
    const ext = file.name.toLowerCase().endsWith('.pptx')
      ? 'pptx'
      : file.name.toLowerCase().endsWith('.docx')
        ? 'docx'
        : null;
    if (!ext) return setUploadError('Only .docx or .pptx templates are supported.');
    if (!/^[a-z0-9][a-z0-9-]{1,60}$/.test(id)) {
      return setUploadError(
        'Id must be lowercase letters/digits/hyphens (2-61 chars), e.g. 1nebula-letterhead.',
      );
    }
    setBusy(true);
    try {
      const fileBase64 = await fileToBase64(file);
      await apiPost(`/api/v1/admin/doc-templates/${id}/versions`, {
        fileBase64,
        ext,
        ...(label ? { label } : {}),
        templateClass,
        entity: entity || null,
        fields: fields
          ? fields
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean)
          : [],
      });
      setId('');
      setLabel('');
      setEntity('');
      setFields('');
      setFile(null);
      load();
    } catch (e) {
      setUploadError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const act = async (fn: () => Promise<unknown>): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      load();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout>
      <div className="mx-auto max-w-4xl px-6 py-8">
        <h1 className="ov-h-page">Document Templates</h1>
        <p className="ov-body mt-1 text-nebula-fg-2">
          Letterheads, proposals and legal templates used by the branded Word/PowerPoint export.
          Uploading a file adds a new version and makes it active immediately — no deploy needed.
        </p>

        {/* Upload */}
        <section className="mt-6 rounded-lg border border-nebula-border-soft bg-white p-4">
          <h2 className="ov-h-section">Upload / new version</h2>
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="ov-meta text-nebula-fg-2">
              Template id
              <input
                value={id}
                onChange={(e) => setId(e.target.value)}
                placeholder="1nebula-letterhead"
                className="mt-1 w-full rounded-md border border-nebula-border px-2 py-1.5 font-ui text-sm"
              />
            </label>
            <label className="ov-meta text-nebula-fg-2">
              Label
              <input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="1Nebula Letterhead"
                className="mt-1 w-full rounded-md border border-nebula-border px-2 py-1.5 font-ui text-sm"
              />
            </label>
            <label className="ov-meta text-nebula-fg-2">
              Class
              <select
                value={templateClass}
                onChange={(e) => setTemplateClass(e.target.value as (typeof CLASSES)[number])}
                className="mt-1 w-full rounded-md border border-nebula-border px-2 py-1.5 font-ui text-sm"
              >
                {CLASSES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
            <label className="ov-meta text-nebula-fg-2">
              Entity (letterheads)
              <input
                value={entity}
                onChange={(e) => setEntity(e.target.value)}
                placeholder="1Nebula"
                className="mt-1 w-full rounded-md border border-nebula-border px-2 py-1.5 font-ui text-sm"
              />
            </label>
            <label className="ov-meta text-nebula-fg-2 sm:col-span-2">
              Field tags (proposals/legal, comma-separated)
              <input
                value={fields}
                onChange={(e) => setFields(e.target.value)}
                placeholder="client_name, attention, re, date"
                className="mt-1 w-full rounded-md border border-nebula-border px-2 py-1.5 font-ui text-sm"
              />
            </label>
            <label className="ov-meta text-nebula-fg-2 sm:col-span-2">
              File (.docx / .pptx)
              <input
                type="file"
                accept=".docx,.pptx"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                className="mt-1 block w-full text-sm"
              />
            </label>
          </div>
          {uploadError && <p className="ov-meta mt-2 text-nebula-error">{uploadError}</p>}
          <button
            type="button"
            disabled={busy}
            onClick={() => void upload()}
            className="mt-3 inline-flex items-center gap-2 rounded-md bg-nebula-primary px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
          >
            {busy && <Loader2Icon className="h-4 w-4 animate-spin" />} Upload version
          </button>
        </section>

        {/* List */}
        <section className="mt-6">
          {loading ? (
            <p className="ov-body text-nebula-fg-3">Loading…</p>
          ) : error ? (
            <p className="ov-body text-nebula-error">{error}</p>
          ) : templates.length === 0 ? (
            <p className="ov-body text-nebula-fg-3">No templates yet — upload one above.</p>
          ) : (
            <div className="space-y-3">
              {templates.map((t) => (
                <div
                  key={t.id}
                  className={`rounded-lg border p-4 ${t.status === 'archived' ? 'border-nebula-border-soft bg-nebula-gray-5/40 opacity-70' : 'border-nebula-border-soft bg-white'}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="ov-body font-medium text-nebula-fg-1">{t.label}</span>
                    <span className="rounded bg-nebula-tint px-1.5 py-0.5 text-[10px] uppercase text-nebula-secondary">
                      {t.templateClass}
                    </span>
                    {t.entity && (
                      <span className="rounded bg-nebula-gray-5 px-1.5 py-0.5 text-[10px] text-nebula-fg-2">
                        {t.entity}
                      </span>
                    )}
                    {t.status === 'archived' && (
                      <span className="rounded bg-nebula-error-bg px-1.5 py-0.5 text-[10px] text-nebula-error">
                        archived
                      </span>
                    )}
                  </div>
                  <p className="ov-meta mt-1 text-nebula-fg-3">
                    <span className="font-mono">{t.id}</span> · .{t.ext} · active v{t.activeVersion}{' '}
                    of {t.versions.length} · updated {t.updatedAt.slice(0, 10)} by {t.lastActor}
                    {t.fields.length > 0 && ` · fields: ${t.fields.join(', ')}`}
                  </p>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <a
                      href={`/api/v1/admin/doc-templates/${t.id}/file`}
                      className="rounded-md border border-nebula-border px-2 py-1 text-xs text-nebula-secondary hover:underline"
                    >
                      Download active
                    </a>
                    {t.versions.length > 1 && (
                      <select
                        value={t.activeVersion}
                        disabled={busy}
                        onChange={(e) =>
                          void act(() =>
                            apiPost(`/api/v1/admin/doc-templates/${t.id}/active`, {
                              version: Number(e.target.value),
                            }),
                          )
                        }
                        className="rounded-md border border-nebula-border px-2 py-1 text-xs"
                      >
                        {t.versions.map((v) => (
                          <option key={v.n} value={v.n}>
                            activate v{v.n}
                          </option>
                        ))}
                      </select>
                    )}
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() =>
                        void act(() =>
                          apiPost(
                            `/api/v1/admin/doc-templates/${t.id}/${t.status === 'archived' ? 'restore' : 'archive'}`,
                          ),
                        )
                      }
                      className="rounded-md border border-nebula-border px-2 py-1 text-xs text-nebula-fg-2 hover:border-nebula-error hover:text-nebula-error"
                    >
                      {t.status === 'archived' ? 'Restore' : 'Archive'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
}
